import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, DollarSign, ShoppingBag, CreditCard, TrendingUp,
  Package, Users, Calendar, ChevronDown,
} from "lucide-react";


// Phase 6A · Unified Sales Dashboard (read-only)
// ---------------------------------------------
// Separate business-reporting surface. Never touches operational
// order queues, fulfillment, picking or inventory. Answers:
//   • How much did I sell?
//   • Which channel produced the sales?
//   • What did I sell (item ranking)?
//   • How much did I sell each day?
// Sales date = `submitted_at`. `entered_at` / `created_at` are
// audit-only and are not used anywhere in this dashboard.

const money = (n) =>
  new Intl.NumberFormat("en-US",
    { style: "currency", currency: "USD" }).format(Number(n || 0));

const PRESETS = [
  ["today",       "Today"],
  ["yesterday",   "Yesterday"],
  ["last_7",      "Last 7 Days"],
  ["last_30",     "Last 30 Days"],
  ["this_month",  "This Month"],
  ["last_month",  "Last Month"],
  ["all_time",    "All Time"],
  ["custom",      "Custom Range"],
];

const CHANNELS = [
  ["all",         "All Sales"],
  ["website",     "CollectorUp.com"],
  ["shopify",     "Shopify"],
  ["store",       "Store / In-Person"],
  ["events",      "Events"],
  ["sales_order", "Sales Orders"],
  ["ebay",        "eBay"],
];

const EVENT_LOCATIONS = [
  ["",                     "All Events"],
  ["haulover_park",        "Haulover Park"],
  ["tropical_park",        "Tropical Park"],
  ["store_saturday_event", "Store Saturday Event"],
];

// Weekday-friendly date formatting for Sales by Day.
const _WEEKDAY_LONG  = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
const _WEEKDAY_SHORT = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const _MONTHS        = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function _parseIsoDate(iso) {
  if (!iso || typeof iso !== "string") return null;
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  return { y, m, d, weekdayIndex: new Date(Date.UTC(y, m - 1, d)).getUTCDay() };
}
function formatDateOnly(iso) {
  const p = _parseIsoDate(iso);
  if (!p) return iso || "—";
  return `${_MONTHS[p.m - 1]} ${p.d}, ${p.y}`;
}
function formatWeekday(iso, { short = false } = {}) {
  const p = _parseIsoDate(iso);
  if (!p) return "";
  return (short ? _WEEKDAY_SHORT : _WEEKDAY_LONG)[p.weekdayIndex];
}
function DateStack({ iso }) {
  const isNarrow = typeof window !== "undefined" && window.innerWidth < 640;
  return (
    <div className="leading-tight" data-testid="sales-day-cell">
      <div className="font-semibold">{formatDateOnly(iso)}</div>
      <div className="text-[11px] text-[var(--smoke)] font-normal">
        {formatWeekday(iso, { short: isNarrow })}
      </div>
    </div>
  );
}

// Items Sold sorting helpers.
function nextSort(key, current) {
  if (current.key !== key) return { key, dir: "desc" };
  return { key, dir: current.dir === "desc" ? "asc" : "desc" };
}
function sortLabel(base, key, current) {
  if (current.key !== key) return base;
  return `${base} ${current.dir === "desc" ? "↓" : "↑"}`;
}
function sortItems(rows, sortState) {
  const k = sortState.key;
  const sign = sortState.dir === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => {
    const av = Number(a[k] || 0);
    const bv = Number(b[k] || 0);
    if (av === bv) return String(a.title || "").localeCompare(String(b.title || ""));
    return (av - bv) * sign;
  });
}

// Generic client-side sort for the other Sales Dashboard tables.
// Reuses the exact same `nextSort` / `sortLabel` UX helpers as
// Items Sold — the only difference is that this helper handles
// mixed text/numeric columns automatically:
//   • both values numbers → numeric compare
//   • otherwise           → String.localeCompare (ISO date strings
//                             sort chronologically because YYYY-MM-DD
//                             lexicographic order == chronological)
function sortRows(rows, sortState) {
  const k = sortState.key;
  const sign = sortState.dir === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => {
    const av = a[k], bv = b[k];
    if (typeof av === "number" && typeof bv === "number") {
      return (av - bv) * sign;
    }
    return String(av ?? "").localeCompare(String(bv ?? "")) * sign;
  });
}


// Phase 2C · Items Sold by Category — nested CATEGORY → SUBCATEGORY →
// CHANNEL / PRODUCT drilldown rendered as a single aligned reporting grid.
// Pure presentation; data shape produced by backend `_finalize_category_agg`.
//
// Alignment contract: EVERY row (header, category, subcategory, channel,
// product) uses the SAME 7-column grid template so numeric columns line up
// vertically regardless of hierarchy depth. Depth is expressed only via
// left-padding inside the first (label) column.
const CAT_GRID_COLS =
  "grid-cols-[28px_minmax(0,1fr)_110px_90px_120px_100px_90px]";

function CatRow({
  depth = 0,
  chevron = null,
  label,
  labelClassName = "",
  subcats = "—",
  units,
  dollars,
  txn,
  avg,
  rowClassName = "",
  onClick = null,
  testid,
  moneyFmt,
  avgFmt,
  title,
}) {
  const padLeft = depth === 0 ? "pl-0" : depth === 1 ? "pl-5" : depth === 2 ? "pl-10" : "pl-14";
  const base =
    `grid ${CAT_GRID_COLS} items-center gap-2 px-3 py-2 text-[12px] ${rowClassName}`;
  const Cmp = onClick ? "button" : "div";
  return (
    <Cmp
      type={onClick ? "button" : undefined}
      onClick={onClick || undefined}
      className={`${base} ${onClick ? "w-full text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50" : ""}`}
      data-testid={testid}>
      <span className="text-[11px] text-[var(--smoke)] w-6 text-center select-none">
        {chevron ?? ""}
      </span>
      <span className={`truncate ${padLeft} ${labelClassName}`} title={title || (typeof label === "string" ? label : undefined)}>
        {label}
      </span>
      <span className="text-right tabular-nums text-[var(--smoke)]">{subcats}</span>
      <span className="text-right tabular-nums">{units}</span>
      <span className="text-right tabular-nums">{moneyFmt(dollars)}</span>
      <span className="text-right tabular-nums">{txn}</span>
      <span className="text-right tabular-nums">{avgFmt(avg)}</span>
    </Cmp>
  );
}

// Section kicker — small uppercase muted divider used inside an
// expanded subcategory to visually separate the CHANNEL SUMMARY
// (total channel breakdown for the subcategory) from the PRODUCTS
// list (per-item rows, each independently expandable to its OWN
// channel breakdown). Uses the same 7-column grid as every data
// row so numeric columns stay perfectly aligned — the label sits
// in the Category column and the numeric cells are left blank.
function SectionKicker({ depth = 1, label, testid, note = null }) {
  const padLeft = depth === 1 ? "pl-5" : "pl-10";
  return (
    <div
      className={`grid ${CAT_GRID_COLS} items-center gap-2 px-3 pt-3 pb-1 text-[10px] uppercase tracking-[0.12em] text-[var(--smoke)] font-semibold bg-white border-t border-gray-100`}
      data-testid={testid}>
      <span />
      <span className={padLeft}>
        {label}
        {note && (
          <span
            className="ml-1.5 text-[var(--smoke)]/70 cursor-help normal-case tracking-normal font-normal"
            title={note}
            aria-label={note}
            data-testid={`${testid}-note`}>
            &#9432;
          </span>
        )}
      </span>
      <span /><span /><span /><span /><span />
    </div>
  );
}

function ItemsByCategory({ data }) {
  const [openCats, setOpenCats] = useState(() => new Set());
  const [openSubs, setOpenSubs] = useState(() => new Set());
  const [openProds, setOpenProds] = useState(() => new Set());
  function toggleCat(key) {
    setOpenCats((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }
  function toggleSub(key) {
    setOpenSubs((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }
  function toggleProd(key) {
    setOpenProds((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }
  const money = (v) => `$${Number(v || 0).toFixed(2)}`;
  const avg = (v) => v == null ? "—" : `$${Number(v).toFixed(2)}`;

  return (
    <div className="border-2 border-[var(--ink)] rounded-xl overflow-hidden"
         data-testid="items-by-category-root">
      <div className="overflow-x-auto">
        <div className="min-w-[760px]">
          {/* Header row — same grid template as all data rows */}
          <div
            className={`grid ${CAT_GRID_COLS} items-center gap-2 px-3 py-2 bg-[var(--paper)] border-b-2 border-[var(--ink)] text-[10px] uppercase tracking-wide text-[var(--smoke)] font-semibold`}
            data-testid="cat-header">
            <span />
            <span className="text-left">Category</span>
            <span className="text-right">Subcategories</span>
            <span className="text-right">Units</span>
            <span className="text-right">Gross Sales</span>
            <span className="text-right">Transactions</span>
            <span className="text-right">Avg / Unit</span>
          </div>

          <div className="divide-y divide-gray-100">
            {data.map((c) => {
              const catOpen = openCats.has(c.category);
              return (
                <div key={c.category} data-testid={`cat-${c.category}`}>
                  <CatRow
                    depth={0}
                    chevron={catOpen ? "▾" : "▸"}
                    label={<span className="font-bold uppercase tracking-wide">{c.category_label}</span>}
                    title={c.category_label}
                    subcats={c.subcategories.length}
                    units={c.units}
                    dollars={c.dollars}
                    txn={c.txn}
                    avg={c.avg_unit_price}
                    rowClassName="bg-white"
                    onClick={() => toggleCat(c.category)}
                    testid={`cat-toggle-${c.category}`}
                    moneyFmt={money}
                    avgFmt={avg}
                  />
                  {/* Hidden testids preserved for existing tests */}
                  <span className="hidden" data-testid={`cat-units-${c.category}`}>{c.units}</span>
                  <span className="hidden" data-testid={`cat-gross-${c.category}`}>{money(c.dollars)}</span>

                  {catOpen && c.subcategories.map((s) => {
                    const subKey = `${c.category}::${s.subcategory}`;
                    const subOpen = openSubs.has(subKey);
                    const hasProducts = s.products && s.products.length > 0;
                    return (
                      <div key={subKey} data-testid={`sub-${subKey}`}>
                        <CatRow
                          depth={1}
                          chevron={subOpen ? "▾" : "▸"}
                          label={<span className="font-semibold">{s.subcategory_label}</span>}
                          title={s.subcategory_label}
                          subcats="—"
                          units={s.units}
                          dollars={s.dollars}
                          txn={s.txn}
                          avg={s.avg_unit_price}
                          rowClassName="bg-gray-50/40"
                          onClick={() => toggleSub(subKey)}
                          testid={`sub-toggle-${subKey}`}
                          moneyFmt={money}
                          avgFmt={avg}
                        />
                        {subOpen && (
                          <>
                            {s.channels && s.channels.length > 0 && (
                              <SectionKicker
                                depth={1}
                                label="Channel Summary"
                                testid={`sub-channel-summary-header-${subKey}`}
                              />
                            )}
                            {s.channels.map((ch) => (
                              <CatRow
                                key={`ch-${ch.channel}`}
                                depth={2}
                                chevron={null}
                                label={<span className="text-[var(--smoke)]">{ch.channel_label}</span>}
                                subcats="—"
                                units={ch.units}
                                dollars={ch.dollars}
                                txn={ch.txn}
                                avg={ch.avg_unit_price}
                                rowClassName="bg-white"
                                testid={`sub-ch-${subKey}-${ch.channel}`}
                                moneyFmt={money}
                                avgFmt={avg}
                              />
                            ))}
                            {hasProducts && (
                              <SectionKicker
                                depth={1}
                                label="Products"
                                testid={`sub-products-header-${subKey}`}
                                note="Transaction counts here are non-additive — one transaction can contain multiple products, so product transactions may sum higher than the subcategory total."
                              />
                            )}
                            {hasProducts && s.products.map((p) => {
                              const prodKey = `${subKey}::${p.product_label}`;
                              const prodChannels = Array.isArray(p.channels)
                                ? p.channels.filter((pc) => (pc.units || 0) > 0 || (pc.dollars || 0) > 0 || (pc.txn || 0) > 0)
                                : [];
                              const prodHasChannels = prodChannels.length > 0;
                              const prodOpen = prodHasChannels && openProds.has(prodKey);
                              return (
                                <div key={`p-${prodKey}`} data-testid={`prod-${prodKey}`}>
                                  <CatRow
                                    depth={2}
                                    chevron={prodHasChannels ? (prodOpen ? "▾" : "▸") : null}
                                    label={<span className="italic text-[var(--smoke)]">{p.product_label}</span>}
                                    title={p.product_label}
                                    subcats="—"
                                    units={p.units}
                                    dollars={p.dollars}
                                    txn={p.txn}
                                    avg={p.avg_unit_price}
                                    rowClassName="bg-white"
                                    onClick={prodHasChannels ? () => toggleProd(prodKey) : null}
                                    testid={`prod-toggle-${prodKey}`}
                                    moneyFmt={money}
                                    avgFmt={avg}
                                  />
                                  {prodOpen && prodChannels.map((pc) => (
                                    <CatRow
                                      key={`pch-${pc.channel}`}
                                      depth={3}
                                      chevron={null}
                                      label={<span className="text-[var(--smoke)]">{pc.channel_label}</span>}
                                      subcats="—"
                                      units={pc.units}
                                      dollars={pc.dollars}
                                      txn={pc.txn}
                                      avg={pc.avg_unit_price}
                                      rowClassName="bg-white"
                                      testid={`prod-ch-${prodKey}-${pc.channel}`}
                                      moneyFmt={money}
                                      avgFmt={avg}
                                    />
                                  ))}
                                </div>
                              );
                            })}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}


// Phase 2C · Items Sold by Category — nested CATEGORY → SUBCATEGORY →
// CHANNEL / PRODUCT drilldown rendered as collapsible cards.  Pure
// presentation; data shape produced by backend `_finalize_category_agg`.
// NOTE: Component defined above; re-declared here to be hoisted into
// the file-scope export order (JSX function references at render time
// resolve via closure, so a single declaration suffices — this comment
// marks the insertion point so the existing render block can call it).

async function _fetch_phase2c_noop() { /* placeholder — see ItemsByCategory above */ }

export default function AdminSalesDashboard() {
  const nav = useNavigate();
  // URL-param hydration so refresh preserves filters (preset + custom range).
  const _qs = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const _initPreset  = _qs.get("preset")  || "last_30";
  const _initStart   = _qs.get("from")    || "";
  const _initEnd     = _qs.get("to")      || "";
  const [preset, setPreset] = useState(_initPreset);
  const [customStart, setCustomStart] = useState(_initStart);
  const [customEnd, setCustomEnd] = useState(_initEnd);
  // Applied range — only Apply flips the dashboard filter to custom
  // dates so typing a date doesn't fire a fetch on every keystroke.
  const [appliedCustom, setAppliedCustom] = useState(
    _initPreset === "custom" && _initStart && _initEnd
      ? { start: _initStart, end: _initEnd } : null
  );
  const [customError, setCustomError] = useState("");
  const [channel, setChannel] = useState("all");
  const [eventLocation, setEventLocation] = useState("");
  const [itemSort, setItemSort] = useState({ key: "qty", dir: "desc" });
  const [byDaySort,     setByDaySort]     = useState({ key: "date",      dir: "desc" });
  const [byChannelSort, setByChannelSort] = useState({ key: "gross",     dir: "desc" });
  const [byEventSort,   setByEventSort]   = useState({ key: "gross",     dir: "desc" });
  const [byPaymentSort, setByPaymentSort] = useState({ key: "collected", dir: "desc" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Drill-down drawer state.
  const [drill, setDrill] = useState(null);  // {dimension, value, label}
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillLimit, setDrillLimit] = useState(50);
  const [drillOffset, setDrillOffset] = useState(0);

  // Keep URL in sync so refresh preserves filter state.
  useEffect(() => {
    const q = new URLSearchParams();
    q.set("preset", preset);
    if (preset === "custom" && appliedCustom) {
      q.set("from", appliedCustom.start);
      q.set("to", appliedCustom.end);
    }
    const next = `${window.location.pathname}?${q.toString()}`;
    window.history.replaceState(null, "", next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, appliedCustom]);

  async function _fetch() {
    if (preset === "custom" && !appliedCustom) {
      // Waiting on Apply — don't fetch yet.
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/admin/sales-dashboard/report", {
        preset, channel,
        start_date: preset === "custom" ? appliedCustom?.start : null,
        end_date:   preset === "custom" ? appliedCustom?.end   : null,
        event_location: channel === "events" && eventLocation
          ? eventLocation : null,
        item_sort: "qty",
        item_limit: 100,
        transactions_limit: 50,
      });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load report.");
    } finally { setLoading(false); }
  }
  useEffect(() => { _fetch(); },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [preset, channel, eventLocation, appliedCustom]);

  function applyCustomRange() {
    setCustomError("");
    if (!customStart || !customEnd) {
      setCustomError("Please pick both From and To dates."); return;
    }
    if (customStart > customEnd) {
      setCustomError("From date cannot be after To date."); return;
    }
    setAppliedCustom({ start: customStart, end: customEnd });
  }

  async function openDrill(dimension, value, label) {
    setDrill({ dimension, value, label });
    setDrillOffset(0);
    await _fetchDrill(dimension, value, 0, drillLimit);
  }
  async function _fetchDrill(dimension, value, offset, limit) {
    setDrillLoading(true);
    try {
      const r = await api.get("/admin/sales-dashboard/drilldown", {
        params: {
          dimension, value,
          preset,
          start_date: preset === "custom" ? appliedCustom?.start : undefined,
          end_date:   preset === "custom" ? appliedCustom?.end   : undefined,
          channel,
          event_location: channel === "events" && eventLocation
            ? eventLocation : undefined,
          limit, offset,
        },
      });
      setDrillData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load drill-down.");
    } finally { setDrillLoading(false); }
  }
  function closeDrill() { setDrill(null); setDrillData(null); setDrillOffset(0); }
  async function changeDrillPage(newOffset, newLimit = drillLimit) {
    setDrillOffset(newOffset);
    setDrillLimit(newLimit);
    if (drill) await _fetchDrill(drill.dimension, drill.value, newOffset, newLimit);
  }


  return (
    <div className="min-h-screen bg-[var(--paper,#faf7ef)]"
         data-testid="admin-sales-dashboard">
      <div className="max-w-6xl mx-auto px-3 md:px-6 py-4 space-y-4">
        <div className="flex items-center justify-between gap-2">
          <button onClick={() => nav("/admin")}
                    className="text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1">
            <ArrowLeft size={12}/> Admin
          </button>
          <h1 className="font-display text-2xl md:text-3xl font-black tracking-tight">
            Unified Sales Dashboard
          </h1>
          <div className="w-16"/>
        </div>

        <div className="rounded-2xl border-2 border-yellow-500 bg-yellow-50 text-xs px-3 py-2"
             data-testid="sales-history-notice">
          Historical records are still being reconstructed. Totals
          reflect sales currently recorded in CollectorUp.
        </div>

        {/* Filters */}
        <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-3 space-y-2"
             data-testid="sales-filters">
          <div className="flex flex-wrap gap-2">
            {CHANNELS.map(([key, label]) => (
              <button key={key}
                      onClick={() => { setChannel(key);
                                          if (key !== "events") setEventLocation(""); }}
                      className={`px-3 py-2 rounded-full border-2 text-xs font-bold
                        ${channel === key
                          ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                          : "border-[var(--ink)] text-[var(--ink)]"}`}
                      data-testid={`sales-channel-${key}`}>
                {label}
              </button>
            ))}
          </div>
          {channel === "events" && (
            <div className="flex flex-wrap gap-2 pt-1"
                 data-testid="sales-event-location-row">
              {EVENT_LOCATIONS.map(([key, label]) => (
                <button key={key || "all"}
                        onClick={() => setEventLocation(key)}
                        className={`px-3 py-1 rounded-full border-2 text-[11px] font-semibold
                          ${eventLocation === key
                            ? "bg-[var(--panini-red,#dc2626)] text-white border-[var(--panini-red)]"
                            : "border-[var(--ink)] text-[var(--ink)]"}`}
                        data-testid={`sales-event-${key || "all"}`}>
                  {label}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center flex-wrap gap-2 pt-1">
            <label className="text-[11px] uppercase text-[var(--smoke)]">Date range</label>
            <select value={preset} onChange={(e) => setPreset(e.target.value)}
                    className="border-2 border-[var(--ink)] rounded-xl px-2 py-1 text-sm"
                    data-testid="sales-date-preset">
              {PRESETS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            {preset === "custom" && (
              <div className="flex items-center flex-wrap gap-2"
                   data-testid="sales-custom-range">
                <label className="text-[11px] uppercase text-[var(--smoke)]">From</label>
                <input type="date" value={customStart}
                       onChange={(e) => setCustomStart(e.target.value)}
                       className="border-2 border-[var(--ink)] rounded-xl px-2 py-1 text-sm"
                       data-testid="sales-custom-from"/>
                <label className="text-[11px] uppercase text-[var(--smoke)]">To</label>
                <input type="date" value={customEnd}
                       onChange={(e) => setCustomEnd(e.target.value)}
                       className="border-2 border-[var(--ink)] rounded-xl px-2 py-1 text-sm"
                       data-testid="sales-custom-to"/>
                <button onClick={applyCustomRange}
                        className="px-3 py-1 rounded-full border-2 border-[var(--ink)] bg-[var(--ink)] text-white text-xs font-bold"
                        data-testid="sales-custom-apply">Apply</button>
                {appliedCustom && !customError && (
                  <span className="text-[11px] text-[var(--smoke)]"
                        data-testid="sales-custom-active">
                    {formatDateOnly(appliedCustom.start)} – {formatDateOnly(appliedCustom.end)}
                  </span>
                )}
                {customError && (
                  <span className="text-[11px] text-[var(--panini-red)]"
                        data-testid="sales-custom-error">{customError}</span>
                )}
              </div>
            )}
          </div>
        </div>

        {loading || !data ? (
          <div className="py-20 text-center text-sm text-[var(--smoke)]"
               data-testid="sales-loading">
            Loading report…
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3"
                 data-testid="sales-kpis">
              <Kpi icon={<DollarSign size={16}/>}     label="Gross Sales"       value={money(data.kpis.gross_sales)}    testid="kpi-gross"/>
              <Kpi icon={<TrendingUp size={16}/>}     label="Sales Tax"         value={money(data.kpis.sales_tax)}      testid="kpi-tax"/>
              <Kpi icon={<CreditCard size={16}/>}     label="Amount Collected"  value={money(data.kpis.amount_collected)} testid="kpi-collected"/>
              <Kpi icon={<ShoppingBag size={16}/>}    label="Sales Transactions"
                   value={data.kpis.transactions}         testid="kpi-txn"
                   help="Number of separate sales/orders."/>
              <Kpi icon={<DollarSign size={16}/>}     label="Average Sale"      value={money(data.kpis.average_sale)}   testid="kpi-avg"/>
              <Kpi icon={<Package size={16}/>}        label="Units Sold"        value={data.kpis.units_sold}           testid="kpi-units"
                   help="Total quantity of merchandise units sold."/>
            </div>

            {/* Feb-2026 · Payments Received — canonical read-side truth
                layer. Fed by `order_services.payment_events.iter_payment_events`
                so Shopify, Sales Order payments[], Quick Sale split-tender,
                legacy payment.verified_at, eBay and spreadsheet imports all
                normalize to one axis. The RECONCILIATION badge is a trust
                indicator — PASS when every event mapped cleanly. */}
            {data.payments_collected && (
              <Section title="Payments Received"
                       testid="payments-collected">
                <div className="flex items-center gap-2 -mt-1 mb-2"
                     data-testid="payments-reconciliation-badge-row">
                  <span className="text-[11px] text-[var(--smoke)] flex-1"
                        data-testid="payments-collected-help">
                    Actual payments received in selected date range · bucketed on
                    the day the money arrived (not the day the order was created).
                    Partial Sales Order payments count on their own date.
                  </span>
                  {data.reconciliation_v2 && (
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase border ${
                        data.reconciliation_v2.status === "PASS"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-amber-50 text-amber-800 border-amber-300"
                      }`}
                      data-testid={`payments-reconciliation-${(data.reconciliation_v2.status || "unknown").toLowerCase()}`}
                      title={
                        data.reconciliation_v2.status === "PASS"
                          ? "Every payment mapped cleanly to a canonical event."
                          : `${data.reconciliation_v2.unmapped_payment_count || 0} record(s) unmapped. Click to inspect.`
                      }>
                      Reconciliation {data.reconciliation_v2.status || "—"}
                      {data.reconciliation_v2.status !== "PASS" && typeof data.reconciliation_v2.unmapped_payment_count === "number" && (
                        <span className="ml-1 opacity-80" data-testid="payments-reconciliation-unmapped-count">
                          · {data.reconciliation_v2.unmapped_payment_count} unmapped
                        </span>
                      )}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3"
                     data-testid="payments-collected-kpis">
                  <Kpi icon={<CreditCard size={16}/>}
                       label="Payments Received"
                       value={money(data.payments_collected.total)}
                       testid="kpi-payments-collected"
                       help="Total money received by payment date, independent of when the order was created." />
                  <Kpi icon={<ShoppingBag size={16}/>}
                       label="Payment Entries"
                       value={data.payments_collected.transaction_count}
                       testid="kpi-payments-count"
                       help="Each payment entry (incl. partials on Sales Orders) is counted once on its received_at date." />
                </div>

                {(data.payments_collected.by_source?.length ?? 0) > 0 && (
                  <div className="mt-3" data-testid="payments-by-source">
                    <div className="text-[10px] uppercase tracking-wider text-[var(--smoke)] mb-1">
                      By Source
                    </div>
                    <div className="rounded-xl border border-[var(--line)] overflow-hidden">
                      {data.payments_collected.by_source.map((s) => (
                        <div key={s.source}
                             className="flex items-center justify-between px-3 py-2 border-b last:border-b-0 border-[var(--line)]"
                             data-testid={`payments-by-source-row-${s.source}`}>
                          <span className="text-sm font-semibold text-[var(--ink)] capitalize">
                            {s.source.replace("_", " ")}
                            <span className="ml-2 text-[11px] font-normal text-[var(--smoke)]">
                              {s.count} payment{s.count === 1 ? "" : "s"}
                            </span>
                          </span>
                          <span className="font-mono text-sm font-bold text-[var(--ink)]">
                            {money(s.amount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(data.payments_collected.by_method?.length ?? 0) > 0 && (
                  <div className="mt-3" data-testid="payments-by-method">
                    <div className="text-[10px] uppercase tracking-wider text-[var(--smoke)] mb-1">
                      By Method
                    </div>
                    <div className="rounded-xl border border-[var(--line)] overflow-hidden">
                      {data.payments_collected.by_method.map((m) => (
                        <div key={m.method}
                             className="flex items-center justify-between px-3 py-2 border-b last:border-b-0 border-[var(--line)]"
                             data-testid={`payments-by-method-row-${m.method}`}>
                          <span className="text-sm font-semibold text-[var(--ink)]">
                            {m.label}
                            <span className="ml-2 text-[11px] font-normal text-[var(--smoke)]">
                              {m.count} payment{m.count === 1 ? "" : "s"}
                            </span>
                          </span>
                          <span className="font-mono text-sm font-bold text-[var(--ink)]">
                            {money(m.amount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(data.payments_collected.by_day?.length ?? 0) > 0 && (
                  <div className="mt-3" data-testid="payments-by-day">
                    <div className="text-[10px] uppercase tracking-wider text-[var(--smoke)] mb-1">
                      By Day
                    </div>
                    <div className="rounded-xl border border-[var(--line)] overflow-hidden">
                      {data.payments_collected.by_day.map((d) => (
                        <div key={d.date}
                             className="flex items-center justify-between px-3 py-2 border-b last:border-b-0 border-[var(--line)]"
                             data-testid={`payments-by-day-row-${d.date}`}>
                          <span className="text-sm font-mono text-[var(--ink)]">
                            {d.date}
                            <span className="ml-2 text-[11px] font-normal text-[var(--smoke)]">
                              {d.count} payment{d.count === 1 ? "" : "s"}
                            </span>
                          </span>
                          <span className="font-mono text-sm font-bold text-[var(--ink)]">
                            {money(d.amount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {data.reconciliation_v2?.unmapped?.length > 0 && (
                  <div className="mt-3" data-testid="payments-reconciliation-unmapped">
                    <div className="text-[10px] uppercase tracking-wider text-amber-700 mb-1">
                      Unmapped records · needs attention
                    </div>
                    <div className="rounded-xl border border-amber-300 overflow-hidden bg-amber-50/50">
                      {data.reconciliation_v2.unmapped.map((u) => (
                        <div key={u.payment_key}
                             className="flex items-center justify-between px-3 py-2 border-b last:border-b-0 border-amber-200"
                             data-testid={`payments-unmapped-row-${u.public_id}`}>
                          <span className="text-sm font-mono text-amber-900">
                            {u.public_id}
                            <span className="ml-2 text-[11px] font-normal text-amber-700">
                              {u.reason}
                            </span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Section>
            )}

            {/* Revenue Reconciliation — Gross + Tax + Shipping + Surcharge + Fees − Discounts == Collected */}
            {data.reconciliation && (
              <Section title="Revenue Reconciliation" testid="sales-reconciliation">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm"
                     data-testid="sales-reconciliation-grid">
                  <ReconCell label="Gross Sales"     value={money(data.reconciliation.subtotal)}  testid="recon-subtotal"/>
                  <ReconCell label="+ Tax"           value={money(data.reconciliation.tax)}       testid="recon-tax"/>
                  <ReconCell label="+ Shipping"      value={money(data.reconciliation.shipping)}  testid="recon-shipping"/>
                  <ReconCell label="+ Surcharge"     value={money(data.reconciliation.surcharge)} testid="recon-surcharge"/>
                  <ReconCell label="+ Handling Fee"  value={money(data.reconciliation.handling_fee)} testid="recon-handling"/>
                  <ReconCell label="− Discounts"     value={money(data.reconciliation.discounts)} testid="recon-discounts"/>
                  <ReconCell label="= Expected Total" value={money(data.reconciliation.expected_total)} testid="recon-expected" strong/>
                  <ReconCell label="Amount Collected" value={money(data.reconciliation.amount_collected)} testid="recon-collected" strong/>
                </div>
                {Math.abs(Number(data.reconciliation.residual || 0)) > 0.5 && (
                  <div className="mt-2 text-[11px] text-[var(--smoke)]"
                       data-testid="recon-residual">
                    Rounding residual: {money(data.reconciliation.residual)}
                  </div>
                )}
              </Section>
            )}

            {/* Sales by channel */}
            {channel === "all" && data.by_channel.length > 0 && (
              <Section title="Sales by Channel" testid="sales-by-channel">
                <SimpleTable
                  rows={sortRows(data.by_channel, byChannelSort)}
                  onRowClick={(r) => openDrill("channel", r.channel, r.label)}
                  rowTestidPrefix="sales-by-channel-row"
                  columns={[
                    { key: "label", label: sortLabel("Channel", "label", byChannelSort),
                      headerTestid: "sales-by-channel-sort-label",
                      onHeaderClick: () => setByChannelSort(nextSort("label", byChannelSort)) },
                    { key: "gross", label: sortLabel("Gross", "gross", byChannelSort),
                      fmt: money, right: true, headerTestid: "sales-by-channel-sort-gross",
                      onHeaderClick: () => setByChannelSort(nextSort("gross", byChannelSort)) },
                    { key: "txn",   label: sortLabel("Sales", "txn", byChannelSort),
                      right: true, headerTestid: "sales-by-channel-sort-txn",
                      onHeaderClick: () => setByChannelSort(nextSort("txn", byChannelSort)) },
                    { key: "units", label: sortLabel("Units", "units", byChannelSort),
                      right: true, headerTestid: "sales-by-channel-sort-units",
                      onHeaderClick: () => setByChannelSort(nextSort("units", byChannelSort)) },
                  ]}/>
              </Section>
            )}

            {/* Events sub-breakdown */}
            {(channel === "events" || channel === "all") &&
             data.by_event_location && data.by_event_location.length > 0 && (
              <Section title="Sales by Event Location" testid="sales-by-event">
                <SimpleTable
                  rows={sortRows(data.by_event_location, byEventSort)}
                  onRowClick={(r) => openDrill("event_location", r.key, r.label)}
                  rowTestidPrefix="sales-by-event-row"
                  columns={[
                    { key: "label", label: sortLabel("Location", "label", byEventSort),
                      headerTestid: "sales-by-event-sort-label",
                      onHeaderClick: () => setByEventSort(nextSort("label", byEventSort)) },
                    { key: "gross", label: sortLabel("Gross", "gross", byEventSort),
                      fmt: money, right: true, headerTestid: "sales-by-event-sort-gross",
                      onHeaderClick: () => setByEventSort(nextSort("gross", byEventSort)) },
                    { key: "txn",   label: sortLabel("Sales", "txn", byEventSort),
                      right: true, headerTestid: "sales-by-event-sort-txn",
                      onHeaderClick: () => setByEventSort(nextSort("txn", byEventSort)) },
                    { key: "units", label: sortLabel("Units", "units", byEventSort),
                      right: true, headerTestid: "sales-by-event-sort-units",
                      onHeaderClick: () => setByEventSort(nextSort("units", byEventSort)) },
                  ]}/>
              </Section>
            )}

            {/* Sales by day */}
            <Section title="Sales by Day" testid="sales-by-day">
              {data.by_day.length === 0 ? (
                <div className="text-sm text-[var(--smoke)]">No sales in this range.</div>
              ) : (
                <SimpleTable
                  rows={sortRows(data.by_day, byDaySort)}
                  onRowClick={(r) => openDrill("day", r.date, formatDateOnly(r.date))}
                  rowTestidPrefix="sales-by-day-row"
                  columns={[
                    { key: "date",  label: sortLabel("Date", "date", byDaySort),
                      render: (v) => <DateStack iso={v} />,
                      headerTestid: "sales-by-day-sort-date",
                      onHeaderClick: () => setByDaySort(nextSort("date", byDaySort)) },
                    { key: "gross", label: sortLabel("Gross", "gross", byDaySort),
                      fmt: money, right: true,
                      headerTestid: "sales-by-day-sort-gross",
                      onHeaderClick: () => setByDaySort(nextSort("gross", byDaySort)) },
                    { key: "txn",   label: sortLabel("Sales", "txn", byDaySort),
                      right: true,
                      headerTestid: "sales-by-day-sort-txn",
                      onHeaderClick: () => setByDaySort(nextSort("txn", byDaySort)) },
                    { key: "units", label: sortLabel("Units", "units", byDaySort),
                      right: true,
                      headerTestid: "sales-by-day-sort-units",
                      onHeaderClick: () => setByDaySort(nextSort("units", byDaySort)) },
                  ]}/>
              )}
            </Section>

            {/* Items sold — click column headers to sort */}
            <Section title="Items Sold" testid="sales-by-item">
              {data.by_item.length === 0 ? (
                <div className="text-sm text-[var(--smoke)]">No items in this range.</div>
              ) : (
                <SimpleTable
                  rows={sortItems(data.by_item, itemSort)}
                  columns={[
                    { key: "title",   label: "Item" },
                    { key: "qty",     label: sortLabel("Qty",   "qty",     itemSort),
                      right: true, headerTestid: "sales-item-sort-qty",
                      onHeaderClick: () => setItemSort(nextSort("qty", itemSort)) },
                    { key: "dollars", label: sortLabel("Sales", "dollars", itemSort),
                      fmt: money, right: true, headerTestid: "sales-item-sort-dollars",
                      onHeaderClick: () => setItemSort(nextSort("dollars", itemSort)) },
                    { key: "txn",     label: sortLabel("Sales #", "txn",   itemSort),
                      right: true, headerTestid: "sales-item-sort-txn",
                      onHeaderClick: () => setItemSort(nextSort("txn", itemSort)) },
                  ]}/>
              )}
            </Section>

            {/* Phase 2C · Items Sold by Category — NEW parallel view.
                Existing Items Sold table above remains unchanged. */}
            {Array.isArray(data.by_category) && data.by_category.length > 0 && (
              <Section title="Items Sold by Category" testid="sales-by-category">
                <ItemsByCategory data={data.by_category}/>
              </Section>
            )}

            {/* Payment methods */}
            <Section title="Payment Methods" testid="sales-by-payment">
              {data.by_payment.length === 0 ? (
                <div className="text-sm text-[var(--smoke)]">No payments in this range.</div>
              ) : (
                <SimpleTable
                  rows={sortRows(data.by_payment, byPaymentSort)}
                  columns={[
                    { key: "label",     label: sortLabel("Method", "label", byPaymentSort),
                      headerTestid: "sales-by-payment-sort-label",
                      onHeaderClick: () => setByPaymentSort(nextSort("label", byPaymentSort)) },
                    { key: "collected", label: sortLabel("Collected", "collected", byPaymentSort),
                      fmt: money, right: true,
                      headerTestid: "sales-by-payment-sort-collected",
                      onHeaderClick: () => setByPaymentSort(nextSort("collected", byPaymentSort)) },
                    { key: "txn",       label: sortLabel("Sales", "txn", byPaymentSort),
                      right: true,
                      headerTestid: "sales-by-payment-sort-txn",
                      onHeaderClick: () => setByPaymentSort(nextSort("txn", byPaymentSort)) },
                  ]}/>
              )}
            </Section>

            {/* Transaction list */}
            <Section title="Transactions" testid="sales-transactions">
              {data.transactions.length === 0 ? (
                <div className="text-sm text-[var(--smoke)]">
                  No transactions in this range.
                </div>
              ) : (
                <div className="divide-y">
                  {data.transactions.map((t) => (
                    <div key={t.public_id} className="py-2 flex items-start justify-between gap-2 text-sm"
                         data-testid={`sales-txn-${t.public_id}`}>
                      <div className="min-w-0">
                        <div className="text-[11px] text-[var(--smoke)]">
                          {String(t.submitted_at || "").slice(0, 10)} · {t.channel_label}
                          {t.event_location ? ` · ${t.event_location}` : ""}
                        </div>
                        <div className="font-semibold line-clamp-1">
                          {t.summary || "—"}
                          {t.customer ? <span className="text-[var(--smoke)] font-normal"> · {t.customer}</span> : null}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-bold">{money(t.total)}</div>
                        <div className="text-[10px] uppercase text-[var(--smoke)]">
                          {t.payment_method || "—"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </>
        )}
      </div>
      <DrillDrawer open={drill} loading={drillLoading} data={drillData}
                   offset={drillOffset} limit={drillLimit}
                   onClose={closeDrill} onPage={changeDrillPage}/>
    </div>
  );
}

function ReconCell({ label, value, testid, strong }) {
  return (
    <div className={`rounded-xl border-2 border-[var(--ink)] px-2 py-1
                    ${strong ? "bg-[var(--ink)] text-white" : "bg-white"}`}
         data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="font-mono font-bold text-sm">{value}</div>
    </div>
  );
}

function Kpi({ icon, label, value, testid, help }) {
  return (
    <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-3"
         data-testid={testid} title={help || undefined}>
      <div className="text-[10px] uppercase tracking-wider text-[var(--smoke)]
                     inline-flex items-center gap-1">
        {icon} {label}
      </div>
      <div className="text-xl md:text-2xl font-black">{value}</div>
    </div>
  );
}

function Section({ title, testid, rhs, children }) {
  return (
    <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-3"
         data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-bold text-sm">{title}</div>
        {rhs}
      </div>
      {children}
    </div>
  );
}

function SortPill({ active, onClick, testid, children }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`px-2 py-1 rounded-full border-2 text-[11px] font-semibold
              ${active
                ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                : "border-[var(--ink)] text-[var(--ink)]"}`}>
      {children}
    </button>
  );
}

function SimpleTable({ rows, columns, onRowClick, rowTestidPrefix }) {
  const clickable = typeof onRowClick === "function";
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[11px] uppercase text-[var(--smoke)]">
            {columns.map((c) => (
              <th key={c.key}
                  data-testid={c.headerTestid}
                  onClick={c.onHeaderClick}
                  className={`py-1 ${c.right ? "text-right" : "text-left"}
                              ${c.onHeaderClick ? "cursor-pointer select-none hover:text-[var(--ink)]" : ""}`}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}
                className={`border-t ${clickable ? "cursor-pointer hover:bg-amber-50" : ""}`}
                onClick={clickable ? () => onRowClick(r) : undefined}
                data-testid={rowTestidPrefix ? `${rowTestidPrefix}-${i}` : undefined}>
              {columns.map((c) => (
                <td key={c.key}
                    className={`py-1 ${c.right ? "text-right font-mono" : ""}`}>
                  {c.render ? c.render(r[c.key], r)
                     : c.fmt ? c.fmt(r[c.key])
                     : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ─── Drill-down drawer ───────────────────────────────────────────
function DrillDrawer({ open, loading, data, offset, limit, onClose, onPage }) {
  if (!open) return null;
  const total = data?.total_count || 0;
  const pageStart = total === 0 ? 0 : (offset + 1);
  const pageEnd = Math.min(offset + (data?.items?.length || 0), total);
  const dimTitle = {
    channel:        "Sales by Channel",
    event_location: "Sales by Event Location",
    day:            "Sales by Day",
  }[open.dimension] || "Drill-down";
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="drill-down-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose}/>
      <div className="w-full md:w-[640px] bg-white border-l-2 border-[var(--ink)] flex flex-col">
        <div className="p-3 border-b-2 border-[var(--ink)] flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase text-[var(--smoke)]">{dimTitle}</div>
            <div className="font-bold text-base truncate"
                 data-testid="drill-title">{open.label}</div>
            <div className="text-xs text-[var(--smoke)] mt-1">
              Transactions: <b data-testid="drill-total-count">{total}</b> ·
              Total: <b data-testid="drill-total-revenue">
                {new Intl.NumberFormat("en-US",{style:"currency",currency:"USD"})
                  .format(data?.total_revenue || 0)}</b>
            </div>
          </div>
          <button onClick={onClose} data-testid="drill-close-btn"
                  className="text-sm font-semibold px-2 py-1 rounded border">Close</button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {loading && <div className="text-sm text-[var(--smoke)]">Loading…</div>}
          {!loading && (data?.items || []).length === 0 && (
            <div className="text-sm text-[var(--smoke)]">No qualifying transactions.</div>
          )}
          {!loading && (data?.items || []).map((t) => (
            <div key={t.public_id || `${t.submitted_at}-${t.summary}`}
                 className="border-2 border-[var(--ink)] rounded-xl p-2 flex items-start gap-2"
                 data-testid={`drill-row-${t.public_id || "x"}`}>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] text-[var(--smoke)]">
                  {String(t.submitted_at || "").slice(0, 10)} · {t.channel_label}
                  {t.event_location ? ` · ${t.event_location}` : ""}
                </div>
                <div className="font-semibold text-sm line-clamp-1">
                  {t.summary || "—"}
                  {t.customer ? <span className="text-[var(--smoke)] font-normal"> · {t.customer}</span> : null}
                </div>
                <div className="text-[11px] text-[var(--smoke)]">
                  {t.public_id} · {t.payment_method || "—"}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-bold">
                  {new Intl.NumberFormat("en-US",{style:"currency",currency:"USD"}).format(t.total)}
                </div>
                {t.detail_url && (
                  <a href={t.detail_url} className="text-[11px] font-semibold text-[var(--panini-red)]"
                     data-testid={`drill-view-${t.public_id}`}>View →</a>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="p-3 border-t-2 border-[var(--ink)] flex items-center justify-between gap-2 text-xs">
          <div>
            Showing <b data-testid="drill-page-info">{pageStart}–{pageEnd} of {total}</b>
          </div>
          <div className="flex items-center gap-2">
            <select value={limit}
                    onChange={(e) => onPage(0, Number(e.target.value))}
                    className="border-2 border-[var(--ink)] rounded-lg px-2 py-1"
                    data-testid="drill-page-size">
              {[25,50,100,200].map((n)=><option key={n} value={n}>{n}/page</option>)}
            </select>
            <button disabled={offset === 0}
                    onClick={() => onPage(Math.max(0, offset - limit))}
                    className="px-2 py-1 border-2 border-[var(--ink)] rounded-lg disabled:opacity-30"
                    data-testid="drill-prev-btn">Prev</button>
            <button disabled={pageEnd >= total}
                    onClick={() => onPage(offset + limit)}
                    className="px-2 py-1 border-2 border-[var(--ink)] rounded-lg disabled:opacity-30"
                    data-testid="drill-next-btn">Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}
